import type { AppLanguage } from '../../i18n';

type LanguageStorage = {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
};

export const appLanguageStorageKey = 'forma-app-language';
export const appLanguageCookieName = 'forma-app-language';

function parseLanguage(value: string | null | undefined): AppLanguage | null {
  return value === 'en' || value === 'pl' ? value : null;
}

function getBrowserLanguageCookie(): string {
  if (typeof document === 'undefined') {
    return '';
  }

  return document.cookie;
}

function readLanguageCookie(cookie: string): AppLanguage | null {
  const prefix = `${appLanguageCookieName}=`;
  const value = cookie
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(prefix))
    ?.slice(prefix.length);

  try {
    return parseLanguage(value ? decodeURIComponent(value) : null);
  } catch {
    return null;
  }
}

function writeLanguageCookie(language: AppLanguage) {
  if (typeof document === 'undefined') {
    return;
  }

  try {
    document.cookie = `${appLanguageCookieName}=${language}; Path=/; Max-Age=31536000; SameSite=Lax`;
  } catch {
    // The in-origin preference still works when cookies are unavailable.
  }
}

export function getBrowserLanguageStorage(): LanguageStorage | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function readStoredAppLanguage(
  storage: LanguageStorage | null = getBrowserLanguageStorage(),
  cookie: string = getBrowserLanguageCookie(),
): AppLanguage | null {
  const cookieLanguage = readLanguageCookie(cookie);

  if (cookieLanguage) {
    return cookieLanguage;
  }

  if (!storage) {
    return null;
  }

  try {
    return parseLanguage(storage.getItem(appLanguageStorageKey));
  } catch {
    return null;
  }
}

export function writeStoredAppLanguage(
  storage: LanguageStorage | null,
  language: AppLanguage,
): void;
export function writeStoredAppLanguage(language: AppLanguage): void;
export function writeStoredAppLanguage(
  storageOrLanguage: LanguageStorage | AppLanguage | null,
  possibleLanguage?: AppLanguage,
) {
  const storage =
    typeof storageOrLanguage === 'string'
      ? getBrowserLanguageStorage()
      : storageOrLanguage;
  const language =
    typeof storageOrLanguage === 'string'
      ? storageOrLanguage
      : possibleLanguage;

  if (!language) {
    return;
  }

  if (storage) {
    try {
      storage.setItem(appLanguageStorageKey, language);
    } catch {
      // Language selection should still work when storage is blocked or full.
    }
  }

  if (typeof storageOrLanguage === 'string') {
    writeLanguageCookie(language);
  }
}
